import React, { useState, useRef, useEffect } from 'react';
import { templates, defaultTemplate } from '../templates';
import { getLink } from '../helpers';
import * as XLSX from 'xlsx';
import JSZip from 'jszip';

// Initial form values, kept at module level so imported configurations can be merged onto them
const INITIAL_FORM_DATA = {
    fullName: 'John Doe',
    position: 'Operation Manager',
    company: defaultTemplate.defaults.company,
    additionalText: defaultTemplate.defaults.additionalText,
    textColor: '#0b5394', // Varsayılan mavi renk
    email: 'john.doe@example.com',

    // Right column rows (can be any contact info)
    rightRow1: {
        label: 'Phone',
        value: '+1 555 123 4567'
    },
    rightRow2: {
        label: 'Mobile',
        value: '+1 555 987 6543'
    },
    rightRow3: {
        label: 'Email',
        value: 'john.doe@example.com'
    },
    rightRow4: {
        label: 'Website',
        value: defaultTemplate.defaults.websiteDisplay
    },

    websiteUrl: defaultTemplate.defaults.websiteUrl,
    logoUrl: defaultTemplate.defaults.logoUrl,
    logoBase64: '',
    logoType: 'base64', // default to embedded image
    hubs: 'Germany - Finland - Malta - Netherlands - Estonia - Turkey - Azerbaijan - Morocco - Dubai - Panama - China - Poland',
    disclaimer: 'This e-mail, including all attachments, is determined for the addressee only. It may contain commercial secrets or confidential information. In cases of accidental receipt, the included information should not be read, copied or misguided. We kindly ask for a short notice, if this email reached you unintended.'
};

// Initial switch values, kept at module level so imported configurations can be merged onto them
const INITIAL_SWITCH_STATES = {
    hubs: true,
    disclaimer: true
};

const EmailSignatureGenerator = () => {
    const [selectedTemplate, setSelectedTemplate] = useState(defaultTemplate.id);
    const [formData, setFormData] = useState(INITIAL_FORM_DATA);

    const [generatedHTML, setGeneratedHTML] = useState('');
    const [copied, setCopied] = useState(false);
    const [importing, setImporting] = useState(false);
    const [fetchingLogo, setFetchingLogo] = useState(false);
    const [logoError, setLogoError] = useState('');
    const [collapsedSections, setCollapsedSections] = useState({
        personalInfo: false,
        contactInfo: false,
        logo: false,
        additionalInfo: false,
        htmlCode: false
    });

    const [switchStates, setSwitchStates] = useState(INITIAL_SWITCH_STATES);

    const textAreaRef = useRef(null);
    const fileInputRef = useRef(null);
    const excelInputRef = useRef(null);
    const configInputRef = useRef(null);

    // The UAE layout uses a fixed brand palette, a dedicated e-mail field and no disclaimer
    const isAvatecUae = selectedTemplate === 'avatec-uae';

    const toggleSection = (section) => {
        setCollapsedSections({
            ...collapsedSections,
            [section]: !collapsedSections[section]
        });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        // For nested properties (rightRow1, rightRow2, etc.)
        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setFormData({
                ...formData,
                [parent]: {
                    ...formData[parent],
                    [child]: value
                }
            });
        } else {
            setFormData({
                ...formData,
                [name]: value
            });
        }
    };

    const handleSwitchToggle = (switchName) => {
        setSwitchStates({
            ...switchStates,
            [switchName]: !switchStates[switchName]
        });
    };

    const handleTemplateChange = (e) => {
        const newTemplateId = e.target.value;
        setSelectedTemplate(newTemplateId);

        // Find the new template
        const newTemplate = templates.find(t => t.id === newTemplateId) || defaultTemplate;

        // Update template-specific defaults
        setFormData(prevData => {
            const baseData = {
                ...prevData,
                company: newTemplate.defaults.company,
                additionalText: newTemplate.defaults.additionalText,
                websiteUrl: newTemplate.defaults.websiteUrl,
                logoUrl: newTemplate.defaults.logoUrl,
                // if we are in embedded mode, clear previous base64 so it will be re-generated
                ...(prevData.logoType === 'base64' ? { logoBase64: '' } : {}),
                rightRow4: {
                    ...prevData.rightRow4,
                    value: newTemplate.defaults.websiteDisplay
                }
            };

            // SmartMarine specific settings
            if (newTemplateId === 'smartmarine') {
                return {
                    ...baseData,
                    hubs: 'Netherlands - Türkiye',
                    rightRow1: { label: 'Email', value: 'john.doe@example.com' },
                    rightRow2: { label: '', value: '' },
                    rightRow3: { label: '', value: '' },
                    rightRow4: { label: 'Website', value: newTemplate.defaults.websiteDisplay }
                };
            }

            // Avatec Marine UAE specific settings.
            // Position is cleared rather than seeded: the UAE signature carries no job title by
            // default, and the input falls back to showing its placeholder.
            if (newTemplateId === 'avatec-uae') {
                return {
                    ...baseData,
                    position: '',
                    email: newTemplate.defaults.email,
                    hubs: newTemplate.defaults.hubs,
                    rightRow1: { ...newTemplate.defaults.rightRow1 },
                    rightRow2: { ...newTemplate.defaults.rightRow2 },
                    rightRow3: { ...newTemplate.defaults.rightRow3 },
                    rightRow4: { ...newTemplate.defaults.rightRow4 }
                };
            }

            // Avatec Marine specific settings.
            // The rows are re-seeded rather than carried over, because the rows the other templates
            // leave behind do not fit this layout: the UAE rows are label-less for the address lines, which
            // would render here as a bare colon, and its row 4 label is 'Tel', not 'Website'.
            return {
                ...baseData,
                hubs: INITIAL_FORM_DATA.hubs,
                rightRow1: { ...INITIAL_FORM_DATA.rightRow1 },
                rightRow2: { ...INITIAL_FORM_DATA.rightRow2 },
                rightRow3: { ...INITIAL_FORM_DATA.rightRow3 },
                rightRow4: {
                    label: INITIAL_FORM_DATA.rightRow4.label,
                    value: newTemplate.defaults.websiteDisplay
                }
            };
        });
    };

    // Convert image URL to Base64
    const convertUrlToBase64 = async () => {
        if (!formData.logoUrl) {
            setLogoError('Please enter a logo URL');
            return;
        }

        setFetchingLogo(true);
        setLogoError('');

        try {
            // Create a new image and set its source to the logo URL
            const img = new Image();

            img.crossOrigin = 'Anonymous'; // Attempt to avoid CORS issues

            img.onload = () => {
                // Create a canvas and draw the image on it
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;

                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0);

                // Get the base64 data URL from the canvas
                const base64 = canvas.toDataURL('image/png');

                // Update the form data
                setFormData({
                    ...formData,
                    logoBase64: base64,
                    logoType: 'base64'
                });

                setFetchingLogo(false);
            };

            img.onerror = () => {
                setLogoError('Failed to load image. Check the URL or CORS policy.');
                setFetchingLogo(false);
            };

            img.src = formData.logoUrl;

        } catch (error) {
            setLogoError('Error converting image: ' + error.message);
            setFetchingLogo(false);
        }
    };

    // Handle file upload for logo
    const handleLogoUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.match('image.*')) {
            setLogoError('Please select an image file');
            return;
        }

        setLogoError('');

        const reader = new FileReader();
        reader.onload = (event) => {
            setFormData({
                ...formData,
                logoBase64: event.target.result,
                logoType: 'base64'
            });
        };

        reader.onerror = () => {
            setLogoError('Error reading file');
        };

        reader.readAsDataURL(file);
    };

    // Reset to URL logo
    const resetToUrlLogo = () => {
        setFormData({
            ...formData,
            logoType: 'url'
        });
        setLogoError('');
    };

    const generateSignature = () => {
        // Find the selected template
        const template = templates.find(t => t.id === selectedTemplate) || defaultTemplate;

        // Use the template generator function to create HTML
        // Pass the form data and switch states to the template generator
        const html = template.generator(formData, switchStates);
        setGeneratedHTML(html);
    };

    // Generate signature on every change or template/switch change for live preview
    useEffect(() => {
        generateSignature();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData, selectedTemplate, switchStates]);

    // Auto-convert default logo to base64 when logoType is base64 and logoBase64 empty.
    // logoBase64 is a dependency so the conversion also re-runs when a template switch clears it
    // while keeping the same logoUrl; the guard below makes the extra run a no-op once it is set.
    useEffect(() => {
        if (formData.logoType === 'base64' && !formData.logoBase64 && formData.logoUrl) {
            convertUrlToBase64();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.logoUrl, formData.logoType, formData.logoBase64]);

    const copyToClipboard = () => {
        textAreaRef.current.select();
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const downloadHtml = () => {
        const element = document.createElement('a');
        const file = new Blob([generatedHTML], { type: 'text/html' });
        element.href = URL.createObjectURL(file);

        // Format the filename using fullName with proper Turkish character handling
        const fileName = formData.fullName
            .trim()                     // Remove leading/trailing spaces
            .replace(/\s+/g, ' ')      // Replace multiple spaces with single space
            .replace(/ı/g, 'i')        // Turkish character replacements
            .replace(/ğ/g, 'g')
            .replace(/ü/g, 'u')
            .replace(/ş/g, 's')
            .replace(/ö/g, 'o')
            .replace(/ç/g, 'c')
            .replace(/İ/g, 'I')
            .replace(/Ğ/g, 'G')
            .replace(/Ü/g, 'U')
            .replace(/Ş/g, 'S')
            .replace(/Ö/g, 'O')
            .replace(/Ç/g, 'C')
            .replace(/ /g, '_')        // Replace spaces with underscores
            .replace(/[^a-zA-Z0-9_]/g, '') // Remove any remaining special characters
            + '.html';                 // Add .html extension

        element.download = fileName;
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
    };

    const openPreview = () => {
        const previewWindow = window.open('', '_blank');
        previewWindow.document.write(generatedHTML);
        previewWindow.document.close();
    };

    const triggerFileInput = () => {
        fileInputRef.current.click();
    };

    // Sanitizes a string to be a safe filename
    const sanitizeFileName = (name) => {
        return name
            .trim()
            .replace(/\s+/g, ' ')
            .replace(/ı/g, 'i')
            .replace(/ğ/g, 'g')
            .replace(/ü/g, 'u')
            .replace(/ş/g, 's')
            .replace(/ö/g, 'o')
            .replace(/ç/g, 'c')
            .replace(/İ/g, 'I')
            .replace(/Ğ/g, 'G')
            .replace(/Ü/g, 'U')
            .replace(/Ş/g, 'S')
            .replace(/Ö/g, 'O')
            .replace(/Ç/g, 'C')
            .replace(/ /g, '_')
            .replace(/[^a-zA-Z0-9_]/g, '') || 'signature';
    };

    // Build formData for each row coming from Excel import
    const buildFormDataForRow = (row) => {
        return {
            ...formData,
            fullName: row['Full Name'] || row.fullName || row['FullName'] || formData.fullName,
            position: row['Position'] || row.position || formData.position,
            company: row['Company'] || row.company || formData.company,
            email: row['Email'] || row.email || row['E-mail'] || formData.email,
            additionalText: row['Additional Text'] || row.additionalText || row.AdditionalText || formData.additionalText,
            textColor: row['Text Color'] || row.textColor || row.TextColor || formData.textColor,

            rightRow1: {
                label: row['Row 1 Label'] || row.rightRow1Label || row.Row1Label || formData.rightRow1.label,
                value: row['Row 1 Value'] || row.rightRow1Value || row.Row1Value || formData.rightRow1.value
            },
            rightRow2: {
                label: row['Row 2 Label'] || row.rightRow2Label || row.Row2Label || formData.rightRow2.label,
                value: row['Row 2 Value'] || row.rightRow2Value || row.Row2Value || formData.rightRow2.value
            },
            rightRow3: {
                label: row['Row 3 Label'] || row.rightRow3Label || row.Row3Label || formData.rightRow3.label,
                value: row['Row 3 Value'] || row.rightRow3Value || row.Row3Value || formData.rightRow3.value
            },
            rightRow4: {
                label: row['Row 4 Label'] || row.rightRow4Label || row.Row4Label || formData.rightRow4.label,
                value: row['Row 4 Value'] || row.rightRow4Value || row.Row4Value || formData.rightRow4.value
            }
        };
    };

    // Build switchStates for each row coming from Excel import
    const buildSwitchStatesForRow = (row) => {
        // Excel values override UI switch states
        const getExcelBooleanValue = (excelValue, fallback) => {
            if (excelValue === 'YES' || excelValue === 'yes' || excelValue === 'true' || excelValue === true) {
                return true;
            }
            if (excelValue === 'NO' || excelValue === 'no' || excelValue === 'false' || excelValue === false) {
                return false;
            }
            // If Excel value is empty/undefined, use UI fallback
            return fallback;
        };

        return {
            hubs: getExcelBooleanValue(row['Hubs Enabled'] || row.hubsEnabled, switchStates.hubs),
            disclaimer: getExcelBooleanValue(row['Disclaimer Enabled'] || row.disclaimerEnabled, switchStates.disclaimer)
        };
    };

    // Build formData for each row with website filtering
    const buildFormDataWithWebsiteFilter = (row) => {
        const baseFormData = buildFormDataForRow(row);

        // If websiteEnabled is false, remove the website from wherever the selected template reads it
        const getWebsiteEnabled = (excelValue) => {
            if (excelValue === 'YES' || excelValue === 'yes' || excelValue === 'true' || excelValue === true) {
                return true;
            }
            if (excelValue === 'NO' || excelValue === 'no' || excelValue === 'false' || excelValue === false) {
                return false;
            }
            // If Excel value is empty/undefined, default to true (show website)
            return true;
        };

        const websiteEnabled = getWebsiteEnabled(row['Website Enabled'] || row.websiteEnabled);
        if (!websiteEnabled) {
            if (isAvatecUae) {
                // the UAE layout renders the website from websiteUrl, not from a contact row.
                // Clearing it also drops the hyperlink around the logo, which uses the same value.
                baseFormData.websiteUrl = '';
            } else if (baseFormData.rightRow4.label.toLowerCase().includes('website')) {
                baseFormData.rightRow4 = { label: '', value: '' };
            }
        }

        return baseFormData;
    };

    // Download an example Excel template for users
    const downloadExampleExcel = () => {
        const wb = XLSX.utils.book_new();
        const wsData = [
            [
                'Full Name',
                'Position',
                'Company',
                'Email',
                'Additional Text',
                'Text Color',
                'Row 1 Label',
                'Row 1 Value',
                'Row 2 Label',
                'Row 2 Value',
                'Row 3 Label',
                'Row 3 Value',
                'Row 4 Label',
                'Row 4 Value',
                'Hubs Enabled',
                'Website Enabled',
                'Disclaimer Enabled'
            ],
            [
                'John Doe',
                'Operation Manager',
                defaultTemplate.defaults.company,
                'john.doe@example.com',
                defaultTemplate.defaults.additionalText,
                '#0b5394',
                'Phone',
                '+1 555 123 4567',
                'Mobile',
                '+1 555 987 6543',
                'Email',
                'john.doe@example.com',
                'Website',
                defaultTemplate.defaults.websiteDisplay,
                'YES',
                'YES',
                'YES'
            ],
            [
                'Jane Smith',
                'Marketing Director',
                defaultTemplate.defaults.company,
                'jane.smith@example.com',
                '',
                '#0b5394',
                'Email',
                'jane.smith@example.com',
                'Phone',
                '+1 555 111 2222',
                '',
                '',
                '',
                '',
                'NO',
                'NO',
                'NO'
            ]
        ];

        const ws = XLSX.utils.aoa_to_sheet(wsData);

        // Style the header row (first row) - make it bold and larger
        const headerStyle = {
            font: { bold: true, sz: 12 },
            fill: { fgColor: { rgb: "E6F3FF" } },
            alignment: { horizontal: "center" }
        };

        // Apply styles to header row (row 1, columns A to Q)
        const range = XLSX.utils.decode_range(ws['!ref']);
        for (let col = range.s.c; col <= range.e.c; col++) {
            const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
            if (!ws[cellAddress]) continue;
            ws[cellAddress].s = headerStyle;
        }

        // Set column widths for better readability
        ws['!cols'] = [
            { wch: 15 }, // Full Name
            { wch: 18 }, // Position  
            { wch: 15 }, // Company
            { wch: 26 }, // Email
            { wch: 20 }, // Additional Text
            { wch: 12 }, // Text Color
            { wch: 15 }, // Row 1 Label
            { wch: 20 }, // Row 1 Value
            { wch: 15 }, // Row 2 Label
            { wch: 20 }, // Row 2 Value
            { wch: 15 }, // Row 3 Label
            { wch: 20 }, // Row 3 Value
            { wch: 15 }, // Row 4 Label
            { wch: 20 }, // Row 4 Value
            { wch: 15 }, // Hubs Enabled
            { wch: 16 }, // Website Enabled
            { wch: 18 }  // Disclaimer Enabled
        ];

        XLSX.utils.book_append_sheet(wb, ws, 'Template');
        const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([wbout], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'signature_template.xlsx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    // Trigger hidden excel file input
    const triggerExcelInput = () => {
        excelInputRef.current.click();
    };

    // Handle Excel upload, generate signatures and download as zip
    const handleExcelUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setImporting(true);

        try {
            const data = await file.arrayBuffer();
            const workbook = XLSX.read(data, { type: 'array' });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const rows = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

            if (rows.length === 0) {
                alert('The selected Excel file is empty.');
                setImporting(false);
                return;
            }

            const zip = new JSZip();
            const template = templates.find((t) => t.id === selectedTemplate) || defaultTemplate;

            // Track filenames to handle duplicates
            const fileNameCounts = {};

            // Rows whose signature could not be generated are skipped and reported at the end
            const failedRows = [];

            rows.forEach((row) => {
                const rowFormData = buildFormDataWithWebsiteFilter(row);
                const rowSwitchStates = buildSwitchStatesForRow(row);

                let html;
                try {
                    html = template.generator(rowFormData, rowSwitchStates);
                } catch (error) {
                    console.error('Error generating signature for row:', rowFormData.fullName, error);
                    failedRows.push(rowFormData.fullName);
                    return;
                }

                // Generate base filename
                let baseFileName = sanitizeFileName(rowFormData.fullName);
                let fileName = baseFileName + '.html';

                // Handle duplicates by adding counter
                if (fileNameCounts[baseFileName]) {
                    fileNameCounts[baseFileName]++;
                    fileName = `${baseFileName}_${fileNameCounts[baseFileName]}.html`;
                } else {
                    fileNameCounts[baseFileName] = 1;
                }

                zip.file(fileName, html);
            });

            if (failedRows.length > 0) {
                alert(`Could not generate signatures for the following rows, they were skipped: ${failedRows.join(', ')}`);
            }

            const content = await zip.generateAsync({ type: 'blob' });
            const url = URL.createObjectURL(content);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'email_signatures.zip';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error processing Excel file:', error);
            alert('An error occurred while processing the Excel file.');
        } finally {
            setImporting(false);
            // Reset the input so that the same file can be selected again if needed
            e.target.value = '';
        }
    };

    // Export current configuration as JSON
    const exportConfiguration = () => {
        const config = {
            selectedTemplate,
            formData,
            switchStates,
            exportDate: new Date().toISOString(),
            version: '1.0'
        };

        const configString = JSON.stringify(config, null, 2);
        const blob = new Blob([configString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'signature_config.json';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    // Trigger config file input
    const triggerConfigInput = () => {
        configInputRef.current.click();
    };

    // Import configuration from JSON file
    const handleConfigImport = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            const text = await file.text();
            const config = JSON.parse(text);

            // Validate config structure
            if (!config.formData || !config.switchStates) {
                alert('Invalid configuration file format.');
                return;
            }

            // Apply configuration
            setSelectedTemplate(config.selectedTemplate || defaultTemplate.id);
            setFormData({ ...INITIAL_FORM_DATA, ...config.formData });
            setSwitchStates({ ...INITIAL_SWITCH_STATES, ...config.switchStates });

            alert('Configuration imported successfully!');
        } catch (error) {
            console.error('Error importing configuration:', error);
            alert('Error reading configuration file. Please check the file format.');
        } finally {
            // Reset input so same file can be selected again
            e.target.value = '';
        }
    };

    return (
        <div className="p-2 sm:p-6 bg-gray-50">
            <div className="max-w-6xl mx-auto">
                <h1 className="text-xl sm:text-2xl font-bold text-blue-700 mb-4">Email Signature Generator</h1>

                {/* Template Selection */}
                <div className="bg-white p-3 sm:p-4 rounded-lg shadow-md mb-4 sm:mb-6">
                    <h2 className="text-base sm:text-lg font-semibold text-blue-600 mb-2 sm:mb-3">Select Template</h2>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Template</label>
                        <select
                            name="template"
                            value={selectedTemplate}
                            onChange={handleTemplateChange}
                            className="w-full p-2 border border-gray-300 rounded-md"
                        >
                            {templates.map(template => (
                                <option key={template.id} value={template.id}>
                                    {template.name} - {template.description}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Bulk Operations & Configuration */}
                <div className="bg-white p-3 sm:p-4 rounded-lg shadow-md mb-4 sm:mb-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

                        {/* Left Side - Bulk Import */}
                        <div>
                            <h3 className="text-base font-semibold text-blue-600 mb-3">Bulk Import from Excel</h3>
                            <div className="flex flex-wrap gap-2 mb-2">
                                <button
                                    onClick={downloadExampleExcel}
                                    className="px-3 py-1 bg-purple-600 text-white text-sm rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
                                >
                                    Download Example Template
                                </button>

                                <button
                                    onClick={triggerExcelInput}
                                    disabled={importing}
                                    className="px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                                >
                                    {importing ? 'Importing...' : 'Import & Download Zip'}
                                </button>

                                <input
                                    ref={excelInputRef}
                                    type="file"
                                    accept=".xlsx,.xls"
                                    onChange={handleExcelUpload}
                                    className="hidden"
                                />
                            </div>
                        </div>

                        {/* Right Side - Configuration Management */}
                        <div>
                            <h3 className="text-base font-semibold text-blue-600 mb-3">Configuration Management</h3>
                            <div className="flex flex-wrap gap-2 mb-2">
                                <button
                                    onClick={exportConfiguration}
                                    className="px-3 py-1 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                                >
                                    Export Configuration
                                </button>

                                <button
                                    onClick={triggerConfigInput}
                                    className="px-3 py-1 bg-orange-600 text-white text-sm rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
                                >
                                    Import Configuration
                                </button>

                                <a
                                    href="/instructions.html"
                                    target="_blank"
                                    className="px-3 py-1 bg-blue-500 text-white text-sm rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 text-decoration-none"
                                >
                                    Usage Guide
                                </a>

                                <input
                                    ref={configInputRef}
                                    type="file"
                                    accept=".json"
                                    onChange={handleConfigImport}
                                    className="hidden"
                                />
                            </div>
                        </div>

                    </div>
                </div>

                {/* Live Preview Section - Full Width */}
                <div className="bg-white p-3 sm:p-4 rounded-lg shadow-md mb-4 sm:mb-6">
                    <div className="flex justify-between items-center mb-3 sm:mb-4">
                        <h2 className="text-base sm:text-lg font-semibold text-blue-600">Live Preview</h2>
                        <button
                            onClick={openPreview}
                            className="px-2 sm:px-4 py-1 bg-green-600 text-white text-xs sm:text-sm rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                        >
                            Open in New Tab
                        </button>
                    </div>
                    <div className="border border-gray-200 p-2 sm:p-4 rounded-md overflow-auto">
                        <div dangerouslySetInnerHTML={{ __html: generatedHTML }} />
                    </div>
                </div>

                {/* Personal Information - Collapsible */}
                <div className="bg-white p-4 rounded-lg shadow-md mb-6">
                    <div
                        onClick={() => toggleSection('personalInfo')}
                        className="flex justify-between items-center cursor-pointer"
                    >
                        <h2 className="text-lg font-semibold text-blue-600">Personal Information</h2>
                        <span className="text-gray-500">
                            {collapsedSections.personalInfo ? '▼' : '▲'}
                        </span>
                    </div>

                    {!collapsedSections.personalInfo && (
                        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                                <input
                                    type="text"
                                    name="fullName"
                                    value={formData.fullName}
                                    onChange={handleChange}
                                    className="w-full p-2 border border-gray-300 rounded-md text-sm"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Position</label>
                                <input
                                    type="text"
                                    name="position"
                                    value={formData.position}
                                    onChange={handleChange}
                                    placeholder="Operation Manager"
                                    className="w-full p-2 border border-gray-300 rounded-md text-sm"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                                <input
                                    type="text"
                                    name="company"
                                    value={formData.company}
                                    onChange={handleChange}
                                    className="w-full p-2 border border-gray-300 rounded-md text-sm"
                                />
                            </div>

                            {isAvatecUae ? (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        className="w-full p-2 border border-gray-300 rounded-md text-sm"
                                    />
                                </div>
                            ) : (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Additional Text</label>
                                    <input
                                        type="text"
                                        name="additionalText"
                                        value={formData.additionalText}
                                        onChange={handleChange}
                                        className="w-full p-2 border border-gray-300 rounded-md text-sm"
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Contact Information - Collapsible */}
                <div className="bg-white p-4 rounded-lg shadow-md mb-6">
                    <div
                        onClick={() => toggleSection('contactInfo')}
                        className="flex justify-between items-center cursor-pointer"
                    >
                        <h2 className="text-lg font-semibold text-blue-600">Contact Information</h2>
                        <span className="text-gray-500">
                            {collapsedSections.contactInfo ? '▼' : '▲'}
                        </span>
                    </div>

                    {!collapsedSections.contactInfo && (
                        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                            {[1, 2, 3, 4].map(rowNum => {
                                // Get the link type using our helper (the UAE layout never linkifies these rows)
                                const linkInfo = isAvatecUae ? null : getLink(
                                    formData[`rightRow${rowNum}`].value,
                                    formData[`rightRow${rowNum}`].label
                                );

                                return (
                                    <div key={rowNum} className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">{isAvatecUae ? `Detail Line ${rowNum} Prefix` : `Row ${rowNum} Label`}</label>
                                            <input
                                                type="text"
                                                name={`rightRow${rowNum}.label`}
                                                value={formData[`rightRow${rowNum}`].label}
                                                onChange={handleChange}
                                                className="w-full p-2 border border-gray-300 rounded-md text-sm"
                                            />
                                        </div>
                                        <div>
                                            <label className="flex justify-between text-sm font-medium mb-1">
                                                <span className="text-gray-700">{isAvatecUae ? `Detail Line ${rowNum} Text` : `Row ${rowNum} Value`}</span>
                                                {!isAvatecUae && (
                                                    <span className="text-xs px-2 py-1 bg-gray-100 rounded-full text-gray-600">
                                                        {linkInfo.type === 'email' && 'Email'}
                                                        {linkInfo.type === 'phone' && 'Phone'}
                                                        {linkInfo.type === 'website' && 'Website'}
                                                        {linkInfo.type === 'text' && 'Text'}
                                                    </span>
                                                )}
                                            </label>
                                            <input
                                                type="text"
                                                name={`rightRow${rowNum}.value`}
                                                value={formData[`rightRow${rowNum}`].value}
                                                onChange={handleChange}
                                                className="w-full p-2 border border-gray-300 rounded-md text-sm"
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Logo - Collapsible */}
                <div className="bg-white p-4 rounded-lg shadow-md mb-6">
                    <div
                        onClick={() => toggleSection('logo')}
                        className="flex justify-between items-center cursor-pointer"
                    >
                        <h2 className="text-lg font-semibold text-blue-600">Logo</h2>
                        <span className="text-gray-500">
                            {collapsedSections.logo ? '▼' : '▲'}
                        </span>
                    </div>

                    {!collapsedSections.logo && (
                        <div className="mt-4">
                            <div className="mb-3">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Logo Source</label>
                                <div className="flex items-center space-x-4">
                                    <label className="inline-flex items-center">
                                        <input
                                            type="radio"
                                            checked={formData.logoType === 'url'}
                                            onChange={resetToUrlLogo}
                                            className="form-radio h-4 w-4 text-blue-600"
                                        />
                                        <span className="ml-2 text-sm text-gray-700">URL</span>
                                    </label>
                                    <label className="inline-flex items-center">
                                        <input
                                            type="radio"
                                            checked={formData.logoType === 'base64'}
                                            readOnly
                                            className="form-radio h-4 w-4 text-blue-600"
                                        />
                                        <span className="ml-2 text-sm text-gray-700">Embedded Image</span>
                                    </label>
                                </div>

                                {/* Logo Source Compatibility Info */}
                                {formData.logoType === 'base64' ? (
                                    <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
                                        <p className="text-xs text-blue-800 font-medium mb-1">Embedded Image (Base64)</p>
                                        <p className="text-xs text-green-700">+ Antispam/antivirus filtreleri tarafindan engellenmez</p>
                                        <p className="text-xs text-green-700">+ Harici sunucuya bagimsiz, resim her zaman eklenir</p>
                                        <p className="text-xs text-amber-700">- Gmail web, Outlook.com ve Yahoo web&apos;de gorunmeyebilir</p>
                                        <p className="text-xs text-amber-700">- E-posta boyutunu arttirir</p>
                                    </div>
                                ) : (
                                    <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
                                        <p className="text-xs text-blue-800 font-medium mb-1">URL Mode</p>
                                        <p className="text-xs text-green-700">+ Tum e-posta istemcilerinde desteklenir</p>
                                        <p className="text-xs text-green-700">+ Kucuk dosya boyutu</p>
                                        <p className="text-xs text-amber-700">- Antispam/guvenlik yazilimlari URL&apos;yi engelleyebilir</p>
                                        <p className="text-xs text-amber-700">- Resim sunucuya bagimlidir, sunucu kapaliysa goruntulenemez</p>
                                    </div>
                                )}
                            </div>

                            {formData.logoType === 'url' ? (
                                <div>
                                    <div className="mb-3">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Logo URL</label>
                                        <input
                                            type="url"
                                            name="logoUrl"
                                            value={formData.logoUrl}
                                            onChange={handleChange}
                                            className="w-full p-2 border border-gray-300 rounded-md text-sm"
                                        />
                                    </div>

                                    <div className="flex space-x-2 mb-4">
                                        <button
                                            onClick={convertUrlToBase64}
                                            disabled={fetchingLogo}
                                            className="px-3 py-1 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                                        >
                                            {fetchingLogo ? 'Converting...' : 'Convert to Embedded Image'}
                                        </button>

                                        <button
                                            onClick={triggerFileInput}
                                            className="px-3 py-1 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                                        >
                                            Upload Image
                                        </button>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            onChange={handleLogoUpload}
                                            className="hidden"
                                            accept="image/*"
                                        />
                                    </div>

                                    {logoError && (
                                        <div className="text-red-500 text-sm mb-3">{logoError}</div>
                                    )}
                                </div>
                            ) : (
                                <div>
                                    <div className="mb-3">
                                        <div className="flex justify-between items-center mb-2">
                                            <label className="block text-sm font-medium text-gray-700">Embedded Image Preview</label>
                                            <button
                                                onClick={resetToUrlLogo}
                                                className="text-xs text-blue-600 hover:text-blue-800"
                                            >
                                                Switch back to URL
                                            </button>
                                        </div>
                                        <div className="p-2 border border-gray-300 rounded-md">
                                            <img
                                                src={formData.logoBase64}
                                                alt="Embedded Logo"
                                                className="max-h-24"
                                            />
                                        </div>
                                    </div>

                                    <div className="mb-4">
                                        <button
                                            onClick={triggerFileInput}
                                            className="px-3 py-1 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                                        >
                                            Upload Different Image
                                        </button>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            onChange={handleLogoUpload}
                                            className="hidden"
                                            accept="image/*"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Additional Info - Collapsible */}
                <div className="bg-white p-4 rounded-lg shadow-md mb-6">
                    <div
                        onClick={() => toggleSection('additionalInfo')}
                        className="flex justify-between items-center cursor-pointer"
                    >
                        <h2 className="text-lg font-semibold text-blue-600">Additional Info</h2>
                        <span className="text-gray-500">
                            {collapsedSections.additionalInfo ? '▼' : '▲'}
                        </span>
                    </div>

                    {!collapsedSections.additionalInfo && (
                        <div className="mt-4 space-y-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Website URL</label>
                                <input
                                    type="url"
                                    name="websiteUrl"
                                    value={formData.websiteUrl}
                                    onChange={handleChange}
                                    className="w-full p-2 border border-gray-300 rounded-md text-sm"
                                />
                            </div>

                            {!isAvatecUae && (
                                <div>
                                    <div className="flex items-center gap-2 mb-1">
                                        <label className="text-sm font-medium text-gray-700">Text Color</label>
                                        <input
                                            type="color"
                                            name="textColor"
                                            value={formData.textColor}
                                            onChange={handleChange}
                                            className="w-8 h-8 p-0 border-0 rounded-md cursor-pointer"
                                        />
                                        <button
                                            onClick={() => {
                                                setFormData({
                                                    ...formData,
                                                    textColor: '#0b5394' // Varsayılan renk
                                                });
                                            }}
                                            className="ml-2 px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                                        >
                                            Reset to Default
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div>
                                <div className="flex items-center gap-2 mb-1">
                                    <label className="text-sm font-medium text-gray-700">Hubs</label>
                                    <div className="relative inline-block w-10 align-middle select-none">
                                        <input
                                            type="checkbox"
                                            id="hubsToggle"
                                            checked={switchStates.hubs}
                                            onChange={() => handleSwitchToggle('hubs')}
                                            className="sr-only"
                                        />
                                        <label
                                            htmlFor="hubsToggle"
                                            className={`block overflow-hidden h-6 rounded-full cursor-pointer ${switchStates.hubs ? 'bg-blue-500' : 'bg-gray-300'
                                                }`}
                                        >
                                            <span
                                                className={`block h-6 w-6 rounded-full bg-white transform transition-transform ${switchStates.hubs ? 'translate-x-4' : 'translate-x-0'
                                                    }`}
                                            ></span>
                                        </label>
                                    </div>
                                </div>
                                <input
                                    type="text"
                                    name="hubs"
                                    value={formData.hubs}
                                    onChange={handleChange}
                                    disabled={!switchStates.hubs}
                                    readOnly={!switchStates.hubs}
                                    className={`w-full p-2 border border-gray-300 rounded-md text-sm ${!switchStates.hubs ? 'bg-gray-100 text-gray-500' : ''
                                        }`}
                                />
                            </div>

                            {!isAvatecUae && (
                                <div>
                                    <div className="flex items-center gap-2 mb-1">
                                        <label className="text-sm font-medium text-gray-700">Disclaimer</label>
                                        <div className="relative inline-block w-10 align-middle select-none">
                                            <input
                                                type="checkbox"
                                                id="disclaimerToggle"
                                                checked={switchStates.disclaimer}
                                                onChange={() => handleSwitchToggle('disclaimer')}
                                                className="sr-only"
                                            />
                                            <label
                                                htmlFor="disclaimerToggle"
                                                className={`block overflow-hidden h-6 rounded-full cursor-pointer ${switchStates.disclaimer ? 'bg-blue-500' : 'bg-gray-300'
                                                    }`}
                                            >
                                                <span
                                                    className={`block h-6 w-6 rounded-full bg-white transform transition-transform ${switchStates.disclaimer ? 'translate-x-4' : 'translate-x-0'
                                                        }`}
                                                ></span>
                                            </label>
                                        </div>
                                    </div>
                                    <textarea
                                        name="disclaimer"
                                        value={formData.disclaimer}
                                        onChange={handleChange}
                                        disabled={!switchStates.disclaimer}
                                        readOnly={!switchStates.disclaimer}
                                        rows="3"
                                        className={`w-full p-2 border border-gray-300 rounded-md text-sm ${!switchStates.disclaimer ? 'bg-gray-100 text-gray-500' : ''
                                            }`}
                                    ></textarea>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* HTML Code - Collapsible */}
                <div className="bg-white p-4 rounded-lg shadow-md">
                    <div
                        onClick={() => toggleSection('htmlCode')}
                        className="flex justify-between items-center cursor-pointer"
                    >
                        <h2 className="text-lg font-semibold text-blue-600">HTML Code</h2>
                        <span className="text-gray-500">
                            {collapsedSections.htmlCode ? '▼' : '▲'}
                        </span>
                    </div>

                    {!collapsedSections.htmlCode && (
                        <div className="mt-4">
                            <textarea
                                ref={textAreaRef}
                                value={generatedHTML}
                                readOnly
                                rows="10"
                                className="w-full p-2 border border-gray-300 rounded-md font-mono text-sm"
                            ></textarea>
                            <div className="mt-4 flex gap-4">
                                <button
                                    onClick={copyToClipboard}
                                    className="px-3 sm:px-4 py-1 sm:py-2 bg-green-600 text-white text-sm sm:font-medium rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                                >
                                    {copied ? 'Copied!' : 'Copy'}
                                </button>
                                <button
                                    onClick={downloadHtml}
                                    className="px-3 sm:px-4 py-1 sm:py-2 bg-blue-600 text-white text-sm sm:font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                >
                                    Download HTML
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EmailSignatureGenerator;